import { apiResource } from '@/util/resources'
import diag from '@/util/messengerDiagnostics'

// Inserts date-separator objects between messages that fall on different calendar days.
// Returns a new flat array: [..., { type:'date-separator', date: Date }, message, ...]
function injectDateSeparators(messages) {
  var result = []
  var lastDateKey = null
  messages.forEach(function (m) {
    var ts = m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp)
    var key = ts.getFullYear() + '-' + ts.getMonth() + '-' + ts.getDate()
    if (key !== lastDateKey) {
      lastDateKey = key
      result.push({ type: 'date-separator', id: 'date-' + key, date: ts })
    }
    result.push(m)
  })
  return result
}

// Shared normalisation: filters and maps raw SDK messages into store shape.
// Excludes historical messages by cutoff date, non-text types, and truly empty
// messages that have neither text nor file attachments.
function normalizeMessages(messages, cutoffDate) {
  var getTime = function (m) { return m.time || (m.channel && m.channel.time) || null }
  var getDirection = function (m) {
    if (m.direction === 'Outbound') return 'incoming'
    if (m.direction === 'Inbound') return 'outgoing'
    if (m.messageType === 'outbound') return 'incoming'
    if (m.messageType === 'inbound') return 'outgoing'
    if (m.from) {
      if (m.from.role === 'Agent' || m.from.role === 'System') return 'incoming'
      if (m.from.role === 'Customer') return 'outgoing'
    }
    if (m.originatingEntity === 'Human') return 'outgoing'
    if (m.originatingEntity === 'Bot') return 'incoming'
    return 'outgoing'
  }
  var isBot = function (m) {
    if (m.originatingEntity === 'Bot') return true
    if (m.from && m.from.role === 'System') return true
    return false
  }
  var isText = function (m) {
    var type = (m.type || '').toLowerCase()
    return type === 'text' || type === 'message'
  }
  return messages
    .filter(function (m) {
      if (!isText(m)) return false
      // Discard messages that belong to a previous closed conversation by
      // comparing against the endedAt of the most recent closed conversation.
      if (cutoffDate) {
        var ts = getTime(m)
        var msgDate = ts ? new Date(ts) : null
        if (msgDate && msgDate <= cutoffDate) return false
      }
      return true
    })
    .map(function (m, idx) {
      var ts = getTime(m)
      // Primary source: m.files (history/restored format)
      var files = (m.files || []).map(function (f) {
        return {
          id: f.id,
          name: f.name,
          url: f.downloadUrl,
          mime: f.mime,
          type: f.type,
          size: f.size
        }
      })
      // Fallback: m.content[].attachment (live messagesReceived format for attachments)
      if (files.length === 0 && Array.isArray(m.content)) {
        m.content.forEach(function (c) {
          var att = c.attachment || (c.contentType === 'Attachment' && c)
          if (att && att.id) {
            files.push({
              id: att.id,
              name: att.filename || att.name || 'attachment',
              url: att.url || att.downloadUrl || null,
              mime: att.mediaType || att.mime || null,
              type: att.type || null,
              size: att.fileSize || att.size || null
            })
          }
        })
      }
      var dir = getDirection(m)
      return {
        id: m.id || ('sdk-msg-' + (ts || Date.now()) + '-' + idx),
        type: 'message',
        text: m.text || (m.content && m.content[0] && m.content[0].statement) || '',
        files: files,
        direction: dir,
        isBot: dir === 'incoming' && isBot(m),
        timestamp: ts ? new Date(ts) : new Date()
      }
    })
    // Keep if it has text OR at least one file attachment
    .filter(function (m) { return m.text.trim() !== '' || m.files.length > 0 })
}

export default {
  namespaced: true,

  state: {
    ready: false,
    connected: false,
    sessionRestored: false,
    historyLoaded: false,
    authError: false,
    conversationOpen: false,
    messages: [],
    pendingMessages: [],
    restoredMessages: null,
    ignoreRestoredUntil: 0,
    closedConversations: [],
    sending: false,
    agentTyping: false,
    pendingAttachments: [],
    uploadQueue: [],
    uploadingFile: null,
    pendingUploadContinue: false,
    uploadProgress: null,
    uploadError: null
  },

  mutations: {
    SET_READY(state, val) {
      state.ready = val
    },
    SET_AUTH_ERROR(state, val) {
      state.authError = val
    },
    SET_CONNECTED(state, val) {
      state.connected = val
    },
    SET_SESSION_RESTORED(state, val) {
      state.sessionRestored = val
    },
    SET_CONVERSATION_OPEN(state, val) {
      state.conversationOpen = val
    },
    SET_HISTORY_LOADED(state, val) {
      state.historyLoaded = val
    },
    SET_CLOSED_CONVERSATIONS(state, conversations) {
      state.closedConversations = conversations
    },
    ADD_MESSAGES(state, messages) {
      // If history hasn't loaded yet, queue the messages so we can filter
      // them correctly against the DB cutoff once fetchHistory completes.
      if (!state.historyLoaded) {
        state.pendingMessages = state.pendingMessages.concat(messages)
        return
      }
      var cutoffDate = state.closedConversations.length > 0 ? state.closedConversations[0].endedAt : null
      var newMessages = normalizeMessages(messages, cutoffDate)
      if (newMessages.length > 0) {
        state.messages = state.messages.concat(newMessages).sort(function (a, b) {
          return a.timestamp - b.timestamp
        })
      }
    },
    SET_SENDING(state, val) {
      state.sending = val
    },
    SET_AGENT_TYPING(state, val) {
      state.agentTyping = val
    },
    ADD_PENDING_ATTACHMENT(state, attachment) {
      if (state.pendingAttachments.some(function (a) { return a.id === attachment.id })) return
      state.pendingAttachments = state.pendingAttachments.concat(attachment)
    },
    ENQUEUE_UPLOADS(state, files) {
      state.uploadQueue = state.uploadQueue.concat(files)
    },
    DEQUEUE_UPLOAD(state) {
      state.uploadQueue = state.uploadQueue.slice(1)
    },
    SET_UPLOADING_FILE(state, file) {
      state.uploadingFile = file
    },
    SET_PENDING_UPLOAD_CONTINUE(state, val) {
      state.pendingUploadContinue = val
    },
    SET_UPLOAD_PROGRESS(state, percentage) {
      if (typeof percentage === 'number' && !isNaN(percentage)) {
        state.uploadProgress = Math.max(0, Math.min(100, percentage))
      } else {
        state.uploadProgress = null
      }
    },
    CLEAR_UPLOAD_PROGRESS(state) {
      state.uploadProgress = null
    },
    SET_ATTACHMENT_UPLOADING(state, id) {
      state.pendingAttachments = state.pendingAttachments.map(function (a) {
        return a.id === id ? Object.assign({}, a, { uploading: true }) : a
      })
    },
    REMOVE_PENDING_ATTACHMENT(state, id) {
      state.pendingAttachments = state.pendingAttachments.filter(function (a) { return a.id !== id })
    },
    SET_UPLOAD_ERROR(state, msg) {
      state.uploadError = msg
    },
    CLEAR_UPLOAD_ERROR(state) {
      state.uploadError = null
    },
    RESET_MESSAGES(state) {
      state.messages = []
    },
    SET_RESTORED_MESSAGES(state, messages) {
      state.restoredMessages = messages
    },
    SET_IGNORE_RESTORED_UNTIL(state, timestamp) {
      state.ignoreRestoredUntil = timestamp
    },
    RESOLVE_RESTORED(state) {
      var cutoffDate = state.closedConversations.length > 0 ? state.closedConversations[0].endedAt : null
      var liveMessages = normalizeMessages(state.restoredMessages, cutoffDate)
      console.log('[messenger] RESOLVE_RESTORED — cutoff:', cutoffDate, '| restored count:', state.restoredMessages.length, '| live after filter:', liveMessages.length)
      state.restoredMessages = null
      state.sessionRestored = liveMessages.length > 0
      if (liveMessages.length > 0) {
        state.messages = state.messages.concat(liveMessages).sort(function (a, b) {
          return a.timestamp - b.timestamp
        })
      }
    },
    FLUSH_PENDING(state) {
      var cutoffDate = state.closedConversations.length > 0 ? state.closedConversations[0].endedAt : null

      // If MessagingService.restored already fired, resolve the active-session decision
      // now that we have the cutoff date from fetchHistory.
      if (state.restoredMessages !== null) {
        var liveMessages = normalizeMessages(state.restoredMessages, cutoffDate)
        console.log('[messenger] FLUSH_PENDING resolving restored — cutoff:', cutoffDate, '| restored count:', state.restoredMessages.length, '| live after filter:', liveMessages.length)
        state.restoredMessages = null
        state.sessionRestored = liveMessages.length > 0
        if (liveMessages.length > 0) {
          state.messages = state.messages.concat(liveMessages)
        }
      }

      // Flush any other live messages that arrived before history was ready
      if (state.pendingMessages.length > 0) {
        var newMessages = normalizeMessages(state.pendingMessages, cutoffDate)
        state.pendingMessages = []
        if (newMessages.length > 0) {
          state.messages = state.messages.concat(newMessages)
        }
      }

      state.messages = state.messages.slice().sort(function (a, b) {
        return a.timestamp - b.timestamp
      })
    },
    RESET(state) {
      state.messages = []
      state.pendingMessages = []
      state.restoredMessages = null
      state.ignoreRestoredUntil = 0
      state.closedConversations = []
      state.connected = false
      state.sessionRestored = false
      state.ready = false
      state.historyLoaded = false
      state.authError = false
      state.conversationOpen = false
      state.agentTyping = false
      state.pendingAttachments = []
      state.uploadQueue = []
      state.uploadingFile = null
      state.pendingUploadContinue = false
      state.uploadProgress = null
      state.uploadError = null
    }
  },

  getters: {
    closedConversations: function (state) {
      return state.closedConversations
    },
    activeConversation: function (state) {
      return state.messages
    },
    activeConversationWithDates: function (state) {
      return injectDateSeparators(state.messages)
    },
    hasActiveConversation: function (state) {
      return state.sessionRestored
    },
    historyLoaded: function (state) {
      return state.historyLoaded
    },
    authError: function (state) {
      return state.authError
    },

  },

  actions: {
    onReady({ commit }) {
      commit('SET_READY', true)
    },

    onSessionStarted({ commit }) {
      commit('SET_CONNECTED', true)
    },

    onSessionRestored({ commit, state }, messages) {
      commit('SET_CONNECTED', true)

      var ignoreRestored = state.ignoreRestoredUntil && Date.now() < state.ignoreRestoredUntil
      if (ignoreRestored) {
        console.log('[messenger] onSessionRestored — ignoring restored payload after manual start')
        commit('SET_IGNORE_RESTORED_UNTIL', 0)
        commit('SET_RESTORED_MESSAGES', null)
        return
      }

      commit('SET_RESTORED_MESSAGES', messages)
      if (state.historyLoaded) {
        // fetchHistory already completed — resolve immediately
        commit('RESOLVE_RESTORED')
      }
      // else: fetchHistory still in flight — FLUSH_PENDING will call RESOLVE_RESTORED
    },

    onConversationDisconnected() {
      // Disconnection only marks the start of the threading window (threadingEndTime
      // is stored in our DB). The conversation remains open until that time expires.
      // The SDK also fires this event during normal session restore as it tears down
      // the pre-auth socket. Nothing should happen here in either case.
      console.log('[messenger] conversationDisconnected — no-op (threading window started)')
    },

    async startConversation({ commit }) {
      commit('SET_IGNORE_RESTORED_UNTIL', Date.now() + 120000)
      try {
        diag.log('store', 'startConversation — dispatching to SDK')
        var result = await window.$genesys.startConversation()
        diag.log('store', 'startConversation — resolved (' + JSON.stringify(result) + ')')
        commit('SET_CONNECTED', true)
        commit('SET_SESSION_RESTORED', true)
        commit('setHasActiveConversation', true, { root: true })
      } catch (err) {
        diag.log('store', 'startConversation — failed: ' + (err && (err.message || String(err))))
        commit('SET_IGNORE_RESTORED_UNTIL', 0)
        throw err
      }
    },

    onConversationStarted({ commit }) {
      commit('SET_CONNECTED', true)
      commit('SET_SESSION_RESTORED', true)
      // Update the debtor-store flag so MessengerView immediately shows
      // "Open active conversation" without needing a page reload.
      commit('setHasActiveConversation', true, { root: true })
    },

    async sendMessage({ state, commit, dispatch }, text) {
      commit('SET_SENDING', true)

      if (!state.connected) {
        try {
          await dispatch('startConversation')
        } catch (e) {
          commit('SET_SENDING', false)
          return false
        }
      }

      try {
        // Resolves true once the server echoes the message back (delivery confirmed),
        // false if sent but unconfirmed. Internally retries on no-echo.
        return await window.$genesys.sendMessageConfirmed(text)
      } catch (err) {
        var msg = typeof err === 'string'
          ? err
          : (err && (err.message || err.error_description || err.error)) || ''
        var isAuthenticatingError = typeof msg === 'string' && msg.toLowerCase().indexOf('while authenticating') !== -1

        if (!isAuthenticatingError) {
          throw err
        }

        console.warn('[messenger] sendMessage failed during auth handshake, retrying once')
        diag.log('store', 'sendMessage — auth-handshake error, retrying after 1s')
        await new Promise(function (resolve) { setTimeout(resolve, 1000) })
        return await window.$genesys.sendMessageConfirmed(text)
      } finally {
        commit('SET_SENDING', false)
      }
    },

    onMessages({ commit, state, dispatch }, messages) {
      commit('SET_AGENT_TYPING', false)

      var agentMessages = messages.filter(function (m) { return m.direction !== 'Inbound' })
      if (agentMessages.length > 0) {
        if (state.conversationOpen) {
          // The user is actively viewing the conversation — mark the incoming agent
          // messages as seen and clear any pending "new messages" banner.
          apiResource.post('api/messenger/seen').catch(function (err) {
            console.warn('[chat] Failed to mark messages as seen', err)
          })
          commit('clearLastUnseenAnsweredMessage', null, { root: true })
        } else {
          // The session is connected in the background but the conversation modal is
          // closed (e.g. user navigated elsewhere). Do NOT mark as seen — surface the
          // "you have received a new message" banner instead.
          var latest = agentMessages[agentMessages.length - 1]
          var preview = (latest && (latest.text || (latest.content && latest.content[0] && latest.content[0].statement))) || true
          commit('setLastUnseenAnsweredMessage', preview, { root: true })
        }
      }

      commit('ADD_MESSAGES', messages)

      // Only start the next upload after the SDK has fully processed messagesReceived.
      // Calling processNextUpload before this causes the SDK's parseTranscript to
      // see the previous attachment still in-flight and phantom-re-upload it.
      if (state.pendingUploadContinue) {
        commit('SET_PENDING_UPLOAD_CONTINUE', false)
        dispatch('processNextUpload')
      }
    },

    onAgentTyping({ commit }) {
      commit('SET_AGENT_TYPING', true)
    },

    onAgentTypingStop({ commit }) {
      commit('SET_AGENT_TYPING', false)
    },

    // Enqueue files for sequential upload (one at a time: requestUpload → fileUploaded → sendMessage → next).
    // Files with non-ASCII names are normalised before queuing.
    queueFilesForUpload({ state, commit, dispatch }, files) {
      var list = Array.prototype.slice.call(files || []).filter(function (f) { return f && f.name })
      if (!list.length) return
      var normalised = list.map(function (f) {
        if (/[^\x20-\x7E]/.test(f.name) && typeof File !== 'undefined') {
          var safe = f.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x20-\x7E]/g, '_').trim() || 'attachment'
          console.log('[messenger] Normalizing filename', f.name, '\u2192', safe)
          return new File([f], safe, { type: f.type, lastModified: f.lastModified })
        }
        return f
      })
      commit('CLEAR_UPLOAD_ERROR')
      commit('ENQUEUE_UPLOADS', normalised)
      if (!state.uploadingFile) {
        dispatch('processNextUpload')
      }
    },

    processNextUpload({ state, commit, dispatch }) {
      if (state.uploadingFile) return
      if (!state.uploadQueue.length) {
        if (state.pendingAttachments.length === 0) commit('CLEAR_UPLOAD_PROGRESS')
        return
      }
      var file = state.uploadQueue[0]
      commit('DEQUEUE_UPLOAD')
      commit('SET_UPLOADING_FILE', file)
      console.log('[messenger] processNextUpload — requesting upload for', file.name)
      window.$genesys.requestUpload(file).catch(function (err) {
        console.warn('[messenger] requestUpload failed for', file.name, err)
        commit('SET_UPLOADING_FILE', null)
        commit('SET_UPLOAD_ERROR', 'messenger.uploadError')
        dispatch('processNextUpload')
      })
    },

    onUploadApproved({ commit, state }, data) {
      var id = data && data.attachmentId
      if (!id) return
      if (!state.uploadingFile) {
        console.warn('[messenger] Ignoring unexpected uploadApproved event', id)
        return
      }
      // Use the actual filename from the file currently being uploaded.
      var name = state.uploadingFile.name || 'attachment'
      commit('ADD_PENDING_ATTACHMENT', { id: id, name: name, uploading: true })
      commit('CLEAR_UPLOAD_ERROR')
    },

    onUploading({ commit }, data) {
      var id = data && data.attachmentId
      if (id) commit('SET_ATTACHMENT_UPLOADING', id)
      commit('SET_UPLOAD_PROGRESS', data && data.percentage)
    },

    onFileUploaded({ state, commit, dispatch }, data) {
      var id = data && data.attachmentId
      if (!id) return
      if (!state.pendingAttachments.some(function (a) { return a.id === id })) {
        console.warn('[messenger] Ignoring fileUploaded for unknown attachmentId', id)
        return
      }
      commit('SET_UPLOADING_FILE', null)
      dispatch('sendSingleAttachment', id)
        .then(function () {
          // Don't call processNextUpload here. The SDK's parseTranscript runs when
          // messagesReceived fires and will phantom-re-upload the current attachment
          // if we start the next requestUpload before that event is handled.
          // Set flag; onMessages will call processNextUpload after ADD_MESSAGES.
          commit('SET_PENDING_UPLOAD_CONTINUE', true)
        })
        .catch(function () {
          // No messagesReceived will arrive on send failure, so continue directly.
          dispatch('processNextUpload')
        })
    },

    onUploadError({ commit, dispatch }, data) {
      var id = data && data.attachmentId
      if (id) commit('REMOVE_PENDING_ATTACHMENT', id)
      var msg = (data && (data.message || data.error)) || 'messenger.uploadError'
      commit('SET_UPLOAD_ERROR', msg)
      commit('SET_UPLOADING_FILE', null)
      dispatch('processNextUpload')
    },

    sendSingleAttachment({ commit, state }, attachmentId) {
      return new Promise(function (resolve, reject) {
        console.log('[messenger] sendSingleAttachment — id', attachmentId)
        window.Genesys('command', 'MessagingService.sendMessage', { attachmentIds: [attachmentId] },
          function (res) {
            console.log('[messenger] sendSingleAttachment succeeded', attachmentId, res)
            commit('REMOVE_PENDING_ATTACHMENT', attachmentId)
            resolve(res)
          },
          function (err) {
            console.warn('[messenger] sendSingleAttachment failed', attachmentId, err)
            commit('REMOVE_PENDING_ATTACHMENT', attachmentId)
            commit('SET_UPLOAD_ERROR', 'messenger.uploadError')
            reject(err)
          }
        )
      })
    },

    // connectionClosed fires during normal SDK startup as the pre-auth socket is torn
    // down, and also on real disconnections which
    // only start the threading window, not end the conversation.
    onConnectionClosed() {
      console.log('[messenger] connectionClosed — no-op (threading window or startup teardown)')
    },

    onAuthError({ commit }) {
      commit('SET_AUTH_ERROR', true)
      commit('SET_READY', false)
      commit('SET_CONNECTED', false)
    },

    // Fetch closed conversation history from the DB.
    // Called once when MessagingService.ready fires.
    fetchHistory({ commit, rootGetters }) {
      var token = rootGetters['access_token']
      if (!token) return

      apiResource.get('api/messenger/history').then(function (response) {
        var conversations = (response.data && response.data.responseObject) || []
        var normalised = conversations.map(function (conv) {
          return {
            id: conv.interactionNr,
            topicNr: conv.interactionTopicNr || null,
            startTime: new Date(conv.startTime),
            endedAt: new Date(conv.threadingEndTime),
            messages: (conv.messages || [])
              .filter(function (m) { return !m.isEvent })
              .map(function (m) {
                return {
                  id: m.messageId,
                  type: 'message',
                  direction: m.isOutbound ? 'incoming' : 'outgoing',
                  text: m.content,
                  senderName: m.collectorLongName || null,
                  isBot: m.isOutbound && !m.collectorLongName,
                  attachmentUrl: m.genesysAttachmentUrl || null,
                  timestamp: new Date(m.sentDate)
                }
              })
          }
        })
        // Most recent first
        normalised.sort(function (a, b) { return b.endedAt - a.endedAt })
        commit('SET_CLOSED_CONVERSATIONS', normalised)
        commit('SET_HISTORY_LOADED', true)
        commit('FLUSH_PENDING')
      }).catch(function (err) {
        console.warn('[chat] fetchHistory failed', err)
        // Still mark loaded so the UI doesn't hang if the endpoint is unavailable
        commit('SET_HISTORY_LOADED', true)
        commit('FLUSH_PENDING')
      })
    }

  }
}
